import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, List, ListOrdered, Heading, Quote, Eye, PenLine } from "lucide-react";

/**
 * Minimal markdown-based rich text editor with a toolbar and live preview toggle.
 * Value is stored as markdown string.
 */
export default function RichEditor({ value, onChange, minRows = 6, testid = "rich-editor" }) {
  const [preview, setPreview] = useState(false);

  const wrap = (before, after = before) => {
    const el = document.getElementById(testid);
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + before.length, end + before.length); }, 0);
  };

  const prefixLine = (prefix) => {
    const el = document.getElementById(testid);
    if (!el) return;
    const start = el.selectionStart;
    const before = value.slice(0, start);
    const after = value.slice(start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + prefix.length, start + prefix.length); }, 0);
    void after;
  };

  const Btn = ({ onClick, title, Icon, tid }) => (
    <button
      type="button" onClick={onClick} title={title} data-testid={tid}
      className="p-2 hover:bg-[color:var(--brand-surface2)] transition-colors border-r hairline"
    ><Icon size={14}/></button>
  );

  return (
    <div className="border hairline bg-white">
      <div className="flex items-center border-b hairline text-neutral-600">
        <Btn onClick={()=>wrap("**")} title="Bold" Icon={Bold} tid={`${testid}-bold`}/>
        <Btn onClick={()=>wrap("*")}  title="Italic" Icon={Italic} tid={`${testid}-italic`}/>
        <Btn onClick={()=>prefixLine("## ")} title="Heading" Icon={Heading} tid={`${testid}-h`}/>
        <Btn onClick={()=>prefixLine("- ")}  title="Bulleted list" Icon={List} tid={`${testid}-ul`}/>
        <Btn onClick={()=>prefixLine("1. ")} title="Numbered list" Icon={ListOrdered} tid={`${testid}-ol`}/>
        <Btn onClick={()=>prefixLine("> ")}  title="Quote" Icon={Quote} tid={`${testid}-quote`}/>
        <div className="flex-1"/>
        <button
          type="button"
          onClick={()=>setPreview(p=>!p)}
          data-testid={`${testid}-preview`}
          className="px-3 py-2 hover:bg-[color:var(--brand-surface2)] transition-colors flex items-center gap-1.5 text-xs"
        >
          {preview ? <><PenLine size={12}/> Edit</> : <><Eye size={12}/> Preview</>}
        </button>
      </div>
      {preview
        ? <div className="p-4 prose-editorial text-sm min-h-[8rem]"><ReactMarkdown>{value || "*Nothing yet*"}</ReactMarkdown></div>
        : <textarea
            id={testid} data-testid={testid}
            value={value} onChange={e=>onChange(e.target.value)}
            rows={minRows}
            className="w-full p-3 bg-transparent focus:outline-none resize-y font-mono text-sm"
            placeholder="Write in markdown — **bold**, *italic*, ## headings, - lists"
          />}
    </div>
  );
}
